<template>
  <AppSearchResults
    v-if="searchResults"
    :title="seo.primaryHeading"
    :rvs="searchResults.results ?? []"
    :total-results="searchResults.totalResults"
    page-source="search"
    :cta="Ctas.SearchResults"
    :page-size="pageSize"
    :current-page="pagination.value.currentPage"
    :search-parameters="searchParameters"
    :breadcrumb="breadcrumb"
  />
</template>

<script setup lang="ts">
import { Ctas } from '~/enums/ctas'
import { captureError } from '~/lib/logger'
import { searchParametersToApiRequest } from '~/lib/search'
import { AppProviderKeys } from '~/providers'
import type { CountryCodes, Place, PlaceAncestor, PlaceSeo, RegionCodes } from '~/types/api'
import type { MenuItem } from '~/types/components/menu-item'
import type { IPPagination } from '~/types/provider/pagination'
import type { ISearchParameters } from '~/types/search/search'

const props = defineProps<{
  place: Place
  seo: PlaceSeo
  pageSize: number
  searchParameters: ISearchParameters
}>()
const pagination = inject<IPPagination>(AppProviderKeys.Pagination) as IPPagination

const { getMarketplaceApiClient } = useApiClients()
const {
  error,
  data: searchResults,
  refresh
} = await useAsyncData('place-rv-search-results', async () => {
  if (props.place === null) return

  const requestPayload = searchParametersToApiRequest(props.searchParameters ?? {})

  if (props.place.placeType === 'Region') {
    requestPayload.RegionCode = props.place.code as RegionCodes
  } else if (props.place.placeType === 'Country') {
    requestPayload.CountryCode = props.place.code as CountryCodes
  } else {
    requestPayload.Latitude = props.place.latitude
    requestPayload.Longitude = props.place.longitude
  }

  requestPayload.Page = pagination.value.currentPage ?? 1
  requestPayload.PageSize = props.pageSize

  const { data: searchResults } = await getMarketplaceApiClient().GET('/Rv/Search', {
    params: {
      query: requestPayload
    }
  })
  return searchResults
})

if (error.value) captureError(toRaw(error.value) ?? new Error('Error getting results for places!'))

if (searchResults.value == null) await localeNavigateTo({ name: 'index' }, { redirectCode: 301 })

const breadcrumb = computed<MenuItem[]>(() => {
  const bc: MenuItem[] = []
  if (!props.place) return bc

  const build = (place: Place) => {
    buildItem(place)
    for (const ancestor of place.ancestry?.slice(1).reverse() ?? []) buildItem(ancestor)
  }

  const buildItem = (place: Place | PlaceAncestor) => {
    const href = getLocalePath({ name: 'rvs-for-sale-keyword-location-place', params: { place: place.route } })
    if (!bc.length) bc.unshift({ name: `${place.name} RVs`, href })
    else bc.unshift({ name: place.name as string, href })
  }

  build(props.place)

  return bc
})

watch(
  () => [props.place, props.pageSize, pagination.value.currentPage, props.searchParameters],
  () => {
    refresh() // Trigger data refresh
  },
  { deep: true } // Deep watch for nested objects like `searchParameters`
)
</script>
